import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const sections = [
    { 
      title: 'Quick Links', 
      links: [
        { name: 'About Us', href: '/about' }, 
        { name: 'Services', href: '/services' }, 
        { name: 'Pricing', href: '/pricing' }, 
        { name: 'Contact', href: '/contact' }
      ] 
    },
    { 
      title: 'For Providers', 
      links: [
        { name: 'Become a Provider', href: '/provider-signup' }, 
        { name: 'Provider Login', href: '/provider-login' }, 
        { name: 'Provider Terms', href: '/provider-terms' }
      ] 
    },
    { 
      title: 'Legal', 
      links: [
        { name: 'Privacy Policy', href: '/privacy-policy' }, 
        { name: 'Customer Terms', href: '/customer-terms' }, 
        { name: 'Cancellation Policy', href: '/cancellation-policy' }, 
        { name: 'Payment Policy', href: '/payment-policy' }
      ] 
    },
  ];

  const socialLinks = [
    { name: 'Facebook', href: '#', icon: 'ri-facebook-fill' },
    { name: 'Twitter', href: '#', icon: 'ri-twitter-x-fill' },
    { name: 'Instagram', href: '#', icon: 'ri-instagram-fill' },
    { name: 'LinkedIn', href: '#', icon: 'ri-linkedin-fill' },
    { name: 'YouTube', href: '#', icon: 'ri-youtube-fill' },
    { name: 'TikTok', href: '#', icon: 'ri-tiktok-fill' },
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5sj2m399ei0h7kmaj6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Thank you for subscribing!' });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to subscribe. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Newsletter Section */}
        <div className="mb-12 pb-12 border-b border-gray-700 animate-on-scroll">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-3">Stay Updated</h3>
            <p className="text-gray-400 mb-6">Subscribe to our newsletter for the latest updates and offers</p>
            <form 
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {message && (
              <p className={`mt-3 text-sm ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {message.text}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Company Info */}
          <div className="animate-on-scroll">
            <h3 className="text-2xl font-bold mb-4 gradient-text">Taskbudy</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Connecting customers with trusted service providers across the UK.
            </p>
          </div>

          {/* Quick Links */}
          <div className="animate-on-scroll animation-delay-200">
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {sections[0].links.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors btn-micro-subtle inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Providers */}
          <div className="animate-on-scroll animation-delay-400">
            <h4 className="text-lg font-semibold mb-4">For Providers</h4>
            <ul className="space-y-3">
              {sections[1].links.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors btn-micro-subtle inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="animate-on-scroll animation-delay-600">
            <h4 className="text-lg font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {sections[2].links.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors btn-micro-subtle inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-700 animate-on-scroll animation-delay-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <p className="text-sm text-gray-400">
                © {new Date().getFullYear()} Taskbudy. All rights reserved.
              </p>
              <span className="hidden sm:inline text-gray-600">|</span>
              <Link
                to="/auth/master-admin-login"
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
              >
                Admin
              </Link>
            </div>
            
            {/* Social Media Icons */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center text-white hover:bg-orange-500 transition-all duration-300 cursor-pointer hover:scale-110"
                  aria-label={social.name}
                >
                  <i className={`${social.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
